import { useMemo, useState } from 'react';
import { ChevronsUpDown, Check } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/atoms/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import type { QbAccount, QbClass } from '@/types/settings';

/**
 * Searchable combobox over the QBO chart of accounts. Stores the account
 * **id** (not free text). Shared between Settings → Accounting GL mapping and
 * the pricebook item form (NOT-97).
 *
 * Legacy free-text values that don't resolve to a known account id are shown
 * as "{value} — not in QuickBooks" instead of being silently orphaned.
 */
export function QbAccountPicker({
  accounts,
  accountTypes,
  value,
  onChange,
  disabled,
}: {
  accounts: QbAccount[];
  accountTypes: string[];
  value?: string;
  onChange: (v: string | undefined) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    if (accountTypes.length === 0) return accounts;
    const wanted = new Set(accountTypes.map((t) => t.toLowerCase()));
    return accounts.filter((a) => wanted.has(a.type.toLowerCase()));
  }, [accounts, accountTypes]);

  const selected = useMemo(() => accounts.find((a) => a.id === value), [accounts, value]);
  const unresolved = !selected && !!value;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between text-left font-normal"
        >
          <span className="truncate">
            {selected ? (
              <>
                {selected.name}
                <span className="ml-1.5 text-xs text-muted-foreground">· {selected.type}</span>
              </>
            ) : unresolved ? (
              <span className="text-amber-600">{value} — not in QuickBooks</span>
            ) : (
              <span className="text-muted-foreground">Pick an account...</span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search accounts..." />
          <CommandList>
            <CommandEmpty>
              {filtered.length === 0
                ? `No ${accountTypes.join('/')} accounts in QuickBooks.`
                : 'No matching accounts.'}
            </CommandEmpty>
            {value && (
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    onChange(undefined);
                    setOpen(false);
                  }}
                >
                  <span className="text-muted-foreground">Clear selection</span>
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup>
              {filtered.map((a) => (
                <CommandItem
                  key={a.id}
                  value={`${a.name} ${a.type} ${a.subType ?? ''}`}
                  onSelect={() => {
                    onChange(a.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${value === a.id ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm">{a.name}</span>
                    <span className="truncate text-[11px] text-muted-foreground">
                      {a.type}
                      {a.subType ? ` · ${a.subType}` : ''}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Searchable combobox over the QBO classes. Stores the class **id**. Same
 * unresolved-legacy-value handling as {@link QbAccountPicker}.
 */
export function QbClassPicker({
  classes,
  value,
  onChange,
  disabled,
}: {
  classes: QbClass[];
  value?: string;
  onChange: (v: string | undefined) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(() => classes.find((c) => c.id === value), [classes, value]);
  const unresolved = !selected && !!value;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between text-left font-normal"
        >
          <span className="truncate">
            {selected ? (
              (selected.fullyQualifiedName ?? selected.name)
            ) : unresolved ? (
              <span className="text-amber-600">{value} — not in QuickBooks</span>
            ) : (
              <span className="text-muted-foreground">Pick a class...</span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search classes..." />
          <CommandList>
            <CommandEmpty>No classes in QuickBooks.</CommandEmpty>
            {value && (
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    onChange(undefined);
                    setOpen(false);
                  }}
                >
                  <span className="text-muted-foreground">Clear selection</span>
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup>
              {classes.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.fullyQualifiedName ?? c.name}
                  onSelect={() => {
                    onChange(c.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${value === c.id ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <span className="truncate text-sm">{c.fullyQualifiedName ?? c.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
